import { apiFetchJson } from '@wundo/client/api.ts';
import type { OnboardingNudgeKey } from '@wundo/shared/onboardingNudge.ts';
import type { TrackedPageRoute } from '@wundo/shared/pageVisit.ts';
import type { Cohort } from '@/types/cohorts.ts';
import type {
  DescriptionsApiResponse,
  QuestionsOverviewApiResponse,
  TypicalQuestionsApiResponse,
} from '@/types/dashboard.ts';
import type { Track } from '@/types/tracks.ts';

/** The counts behind the dashboard's figures. */
export interface DashboardData {
  total_users: number;
  active_tracks: number;
  total_sessions: number;
  total_session_duration_minutes: number;
  coaching_questions: number;
}

/** Which cohort and track the numbers are about, when they are about one. */
export interface DashboardMeta {
  cohort?: Cohort;
  track?: Track;
  organisation_id?: string;
}

export interface DashboardApiResponse {
  data: DashboardData;
  meta: DashboardMeta;
}

/**
 * What the dashboard, the questions overview and its two LLM endpoints all
 * scope themselves by: one cohort, or the whole organisation.
 */
export interface DashboardScope {
  cohortId?: string;
  organisationId?: string;
}

function scopeParams({ cohortId, organisationId }: DashboardScope): string {
  const params = new URLSearchParams();
  if (cohortId) params.append('cohort_id', cohortId);
  if (organisationId) params.append('organisation_id', organisationId);
  return params.toString();
}

function scoped(path: string, scope: DashboardScope): string {
  const query = scopeParams(scope);
  return query ? `${path}?${query}` : path;
}

export function fetchDashboard(scope: DashboardScope): Promise<DashboardApiResponse> {
  return apiFetchJson<DashboardApiResponse>(scoped('/dashboards', scope));
}

export function fetchQuestionsOverview(
  scope: DashboardScope,
): Promise<QuestionsOverviewApiResponse> {
  return apiFetchJson<QuestionsOverviewApiResponse>(
    scoped('/dashboards/questions-overview', scope),
  );
}

export function fetchQuestionDescriptions(
  scope: DashboardScope,
): Promise<DescriptionsApiResponse> {
  return apiFetchJson<DescriptionsApiResponse>(
    scoped('/dashboards/questions-overview/descriptions', scope),
  );
}

export function fetchTypicalQuestions(
  scope: DashboardScope,
): Promise<TypicalQuestionsApiResponse> {
  return apiFetchJson<TypicalQuestionsApiResponse>(
    scoped('/dashboards/questions-overview/typical-questions', scope),
  );
}

/** One daily snapshot of one metric. `period_end` is exclusive. */
export interface MetricDataPoint {
  metric: string;
  value: number;
  period_start: string;
  period_end: string;
  granularity: string;
}

export interface MetricsHistoryResponse {
  data: MetricDataPoint[];
}

/** Lifetime totals for one onboarding nudge. */
export interface NudgeKeyCounts {
  nudge_key: OnboardingNudgeKey;
  shown: number;
  dismissed: number;
  completed: number;
}

export interface MetricStatsResponse {
  activation: { activated: number; total: number };
  sessions: {
    total: number;
    per_messaging_user_per_week: number | null;
    per_messaging_user_per_month: number | null;
    average_per_messaging_user: number | null;
    distribution: { sessions: number; users: number }[];
  };
  nps: { score: number | null; average: number | null; responses: number };
  nudges: NudgeKeyCounts[];
}

/** The window both metrics-history endpoints are asked about. */
export interface MetricsRange {
  dateFrom: string;
  dateTo: string;
  organisationIds: string[];
}

function rangeParams({
  dateFrom,
  dateTo,
  organisationIds,
}: MetricsRange): URLSearchParams {
  const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
  if (organisationIds.length > 0) {
    params.set('organisation_ids', organisationIds.join(','));
  }
  return params;
}

export function fetchMetricsHistory(
  range: MetricsRange,
): Promise<MetricsHistoryResponse> {
  const params = rangeParams(range);
  params.set('granularity', 'daily');
  return apiFetchJson<MetricsHistoryResponse>(`/dashboards/metrics-history?${params}`);
}

export function fetchMetricStats(range: MetricsRange): Promise<MetricStatsResponse> {
  return apiFetchJson<MetricStatsResponse>(
    `/dashboards/metrics-history/stats?${rangeParams(range)}`,
  );
}

/** How one tracked web app page was used by the people in scope. */
export interface PageUsageRow {
  route: TrackedPageRoute;
  users: number;
  users_last_30_days: number;
  total_visits: number;
}

export interface PageUsageResponse {
  data: {
    /** People in scope who opened the web app at least once: every page's base. */
    total_users: number;
    pages: PageUsageRow[];
  };
  meta: { organisation_id: string | null };
}

export interface PageUsageUser {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

export interface PageUsageVisitor extends PageUsageUser {
  visit_count: number;
  last_visit_at: string;
}

export interface PageUsageUsersResponse {
  data: {
    used: PageUsageVisitor[];
    /** Opened the web app, but never this page. */
    never: PageUsageUser[];
  };
  meta: { organisation_id: string | null };
}

export function fetchPageUsage(scope: DashboardScope): Promise<PageUsageResponse> {
  return apiFetchJson<PageUsageResponse>(scoped('/dashboards/page-usage', scope));
}

export function fetchPageUsageUsers(
  scope: DashboardScope,
  route: TrackedPageRoute,
): Promise<PageUsageUsersResponse> {
  const params = new URLSearchParams(scopeParams(scope));
  params.set('route', route);
  return apiFetchJson<PageUsageUsersResponse>(`/dashboards/page-usage/users?${params}`);
}
